import React from 'react';
import { Button, Tab, Form } from 'react-bootstrap';
import {
    CheckBoxModal,
    RejectModal,
    ExamStatus,
    ExamApplicationStatus,
    ExamType,
    PracticalQuestionsTab,
    TheoreticalQuestionsTab,
    AnimatedTabs,
    Textarea,
} from 'components';
import { IExamDetails, IExamSessionDetails } from 'components/models';

const ApplicationExamDetails = ({
    examDetails,
    saveRecommendation,
    chairman,
    recommendations,
    examSessionDetails,
    applicantStatus,
    onClose,
    finalized,
}: IProps) => {
    const initialState: IState = {
        showRecommendation: false,
        recommendation: recommendations,
    };

    const getTab = () => {
        if ((recommendations || applicantStatus === ExamApplicationStatus.NotPassed) && chairman) {
            return TabKey.Recommendation;
        }

        if (examSessionDetails && examSessionDetails.practicalQuestions && showQuestions) {
            return TabKey.Practical;
        }

        if (examSessionDetails && examSessionDetails.theoreticalQuestions && showQuestions) {
            return TabKey.Theory;
        }

        return TabKey.Info;
    };

    const [activeTab, setActiveTab] = React.useState(getTab());
    const [showRecommendation, setShowRecommendation] = React.useState(initialState.showRecommendation);
    const [recommendation, setRecommendation] = React.useState(initialState.recommendation);
    const showQuestions = examDetails.type !== ExamType.Recertification;

    React.useEffect(() => {
        setActiveTab(getTab());
    }, [examDetails]);

    React.useEffect(() => {
        if (recommendations !== recommendation) {
            setRecommendation(recommendations);
            setActiveTab(getTab());
        }
    }, [recommendations, examDetails]);

    const canChangeRecommendation = () => {
        switch (examDetails.status) {
            case ExamStatus.Finished:
            case ExamStatus.FinalizedResults:
                return true;
            default:
                return false;
        }
    };

    const renderRecommendationModal = () => (
        <RejectModal
          show={showRecommendation}
          title="Рекомендация для заключения"
          placeholder="Напишите рекомендацию"
          submitLabel="Сохранить"
          textAreaValue={recommendation}
          onChangeTextArea={setRecommendation}
          onHide={() => setShowRecommendation(false)}
          onSubmit={() => {
                saveRecommendation(recommendation);
                setShowRecommendation(false);
            }}
        />
    );

    const renderCloseButton = () => onClose && <Button variant="outline-secondary" className="ml-4" onClick={onClose}>Отмена</Button>;

    return (
        <section>
            {examSessionDetails ? (
                <AnimatedTabs
                  id="applicationExamDetailsTabs"
                  activeKey={activeTab}
                  onSelect={(key: string) => setActiveTab(key as TabKey)}
                  inModal
                >
                    {chairman && (recommendations || applicantStatus === ExamApplicationStatus.NotPassed) ? (
                        <Tab eventKey={TabKey.Recommendation} title="Рекомендация" className="tab mt-4 p-0">
                            <Form className="pt-4">
                                <Textarea
                                  name="recommendation"
                                  placeholder="Рекомендация"
                                  value={recommendation}
                                  onChange={value => setRecommendation(value)}
                                  rows={7}
                                  style={{ maxWidth: '896px' }}
                                  required
                                />
                                {canChangeRecommendation() ? (
                                    <div style={{ marginTop: '70px', marginBottom: '40px' }}>
                                        <Button
                                          type="submit"
                                          style={{ minWidth: '200px' }}
                                          onClick={() => {
                                                if (recommendation) {
                                                    saveRecommendation(recommendation);
                                                    if (onClose) {
                                                        onClose();
                                                    }
                                                }
                                            }}
                                        >
                                            Сохранить и закрыть
                                        </Button>
                                        {renderCloseButton()}
                                    </div>
                                ) : null}
                            </Form>
                        </Tab>
                    ) : null}

                    {examSessionDetails.practicalQuestions && showQuestions ? (
                        <Tab eventKey={TabKey.Practical} title="Практика" className="tab mt-4 p-0">
                            <PracticalQuestionsTab examSessionDetails={examSessionDetails} finalized={finalized} applicantStatus={applicantStatus} />
                        </Tab>
                    ) : null}

                    {showQuestions ? (
                        <Tab eventKey={TabKey.Theory} title="Теория" className="tab mt-4 p-0">
                            <TheoreticalQuestionsTab examSessionDetails={examSessionDetails} finalized={finalized} />
                        </Tab>
                    ) : null}
                </AnimatedTabs>
            ) : null}

            {!recommendations && chairman && examSessionDetails && (
                <>
                    {!finalized && applicantStatus !== ExamApplicationStatus.NotPassed && (
                        <div className="mt-5 mb-5">
                            <Button onClick={() => setShowRecommendation(true)}>{(examSessionDetails.manualCheck) ? 'Финализировать результат' : 'Написать рекомендацию'}</Button>
                            {renderCloseButton()}
                        </div>
                    )}
                    {examSessionDetails.manualCheck && applicantStatus === ExamApplicationStatus.Passed && !finalized ? (
                        <CheckBoxModal
                          show={showRecommendation}
                          title="Финализировать результат"
                          textAreaTitle="Напишите рекомендацию для заключения:"
                          submitLabel="Сохранить результат"
                          textAreaValue={recommendation}
                          onChangeTextArea={setRecommendation}
                          onHide={() => setShowRecommendation(false)}
                          onSubmit={(passed) => {
                                saveRecommendation(recommendation, passed);
                                setShowRecommendation(false);
                            }}
                        />
                    ) : (
                            renderRecommendationModal()
                        )}
                </>
            )}

            {recommendations && chairman && (
                renderRecommendationModal()
            )}
        </section>
    );
};

export default ApplicationExamDetails;

export interface IProps {
    examDetails: IExamDetails;
    chairman: boolean | null;
    recommendations: string;
    examSessionDetails: IExamSessionDetails | null;
    applicantStatus: ExamApplicationStatus;
    finalized: boolean;
    saveRecommendation: (recommendation: string, passed?: boolean) => void;
    onClose: () => void;
}

interface IState {
    showRecommendation: boolean;
    recommendation: string;
}

enum TabKey {
    Info = 'info',
    Theory = 'theory',
    Practical = 'practical',
    Recommendation = 'recommentadion'
}
